/**
 * RenameChildDialog component
 * Accessible modal form for renaming a child's display name from the parent dashboard.
 *
 * Implements US-004 (T4): Rename-a-child modal dialog.
 *
 * Overlay pattern reused from DemoLimitPopup.tsx (role=dialog, aria-modal, Escape-to-close,
 * focus management). No shadcn Dialog primitive used — project has none (ui/).
 *
 * TO-LL-044 enforcement: PATCH /children/{childId} body sends only { displayName }
 * (exact camelCase key matching children.py line 165). userId and username are never
 * sent or modified (children.py _handle_rename verified line 147: explicitly immutable).
 * TO-LL-036 enforcement: rename errors surfaced inline; dialog stays open on 400/403/404;
 * never swallowed or hidden behind a success-close.
 *
 * Reused components:
 * - Input, Label, Button (src/components/ui/)
 * - DemoLimitPopup overlay shell pattern
 * - useChildMutations.rename (T2)
 *
 * AC-3: rename sends PATCH /children/{childId} with only {displayName};
 *        dialog closes and grid shows new name on 200; child login unaffected.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useChildMutations } from '@/hooks/useChildMutations';
import type { ChildSummary } from '@/types/coins';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RenameChildDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /**
   * The child being renamed. Must be non-null when isOpen is true.
   * Dialog renders null when child is null — safe if grid refetch removes the child.
   */
  child: ChildSummary | null;
  /** Callback to close the dialog */
  onClose: () => void;
}

// ─── RenameChildDialog (main export) ─────────────────────────────────────────

/**
 * RenameChildDialog — overlay modal for renaming a child's display name.
 *
 * Pre-filled with child.displayName; single Input field + Save / Cancel.
 * Sends PATCH /children/{childId} with body { displayName } ONLY.
 * userId and Cognito username remain immutable (AC-3).
 *
 * States:
 *  - Idle: field editable, Save active when value differs from original
 *  - Submitting (rename.isPending): buttons disabled, "Saving..." label
 *  - Error (rename.error or clientError): inline red alert, dialog stays open (TO-LL-036)
 *  - Success (rename.isSuccess): dialog closes; dashboard refetches via ['parent','dashboard']
 */
export function RenameChildDialog({ isOpen, child, onClose }: RenameChildDialogProps) {
  const { rename } = useChildMutations();

  // ─── Form state ───────────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);

  // Focus target: display name input
  const inputRef = useRef<HTMLInputElement>(null);

  // ─── Reset state when dialog opens or child changes ──────────────────────
  useEffect(() => {
    if (isOpen && child) {
      setDisplayName(child.displayName);
      setClientError(null);
      rename.reset();
      // Focus and select the existing name so the parent can immediately type
      const timer = setTimeout(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.select();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, child]);

  // ─── Close on success (AC-3) ──────────────────────────────────────────────
  useEffect(() => {
    if (rename.isSuccess) {
      onClose();
    }
  }, [rename.isSuccess, onClose]);

  // ─── Keyboard handler: Escape closes ─────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !rename.isPending) {
        onClose();
      }
    },
    [onClose, rename.isPending]
  );

  // ─── Form submission ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!child) return;
      setClientError(null);

      const trimmedName = displayName.trim();
      if (!trimmedName) {
        // TO-LL-036: surface validation failure; do not call API
        setClientError('Display name cannot be empty.');
        return;
      }
      if (trimmedName.length > 50) {
        setClientError('Display name must be 50 characters or fewer.');
        return;
      }

      // TO-LL-044: body sends only {displayName} — userId/username never touched (AC-3)
      // TO-LL-036: errors propagate from renameChild; no try/catch here
      rename.mutate({ childId: child.userId, displayName: trimmedName });
    },
    [child, displayName, rename]
  );

  // Render nothing when not open or child not provided
  if (!isOpen || !child) return null;

  const errorMessage = rename.error?.message ?? clientError;
  const isSaveDisabled = rename.isPending || displayName.trim() === '';

  return (
    /* Overlay — same pattern as DemoLimitPopup (role=dialog, aria-modal, fixed inset-0 z-50) */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-child-dialog-title"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget && !rename.isPending) {
          onClose();
        }
      }}
    >
      {/* Dialog card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 w-full max-w-sm mx-4">
        {/* Title */}
        <h2
          id="rename-child-dialog-title"
          className="text-lg md:text-xl font-bold text-gray-900 mb-1"
        >
          Rename {child.displayName}
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Only the display name changes — {child.displayName}'s login is not affected.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Display name field — pre-filled with current name */}
          <div className="space-y-1">
            <Label htmlFor="rename-child-display-name" className="text-sm font-medium text-gray-700">
              Display name <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <Input
              id="rename-child-display-name"
              ref={inputRef}
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter a display name"
              maxLength={50}
              required
              disabled={rename.isPending}
              autoComplete="off"
              className="h-11"
            />
          </div>

          {/* Inline error message — surfaced, never swallowed (TO-LL-036) */}
          {errorMessage && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
            >
              {errorMessage}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={isSaveDisabled}
              className="flex-1 h-11"
            >
              {rename.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={rename.isPending}
              onClick={onClose}
              className="flex-1 h-11"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
